import React, { useEffect, useMemo, useState } from "react";
import IconDefinition from "../iconDefinition/IconDefinition";

interface LegendItem {
  key: string;
  liftType: string;
  icon: string;
  label: string;
}

const LEGEND_ITEMS: LegendItem[] = [
  { key: "el-up", liftType: "ELEVATOR", icon: "elUp", label: "Elevator Moving Up" },
  { key: "el-down", liftType: "ELEVATOR", icon: "elDown", label: "Elevator Moving Down" },
  { key: "el-closed", liftType: "ELEVATOR", icon: "elFrontCloseRearClose", label: "Doors Closed" },
  {
    key: "el-front-open",
    liftType: "ELEVATOR",
    icon: "elFrontOpenRearClose",
    label: "Front Door Open",
  },
  {
    key: "el-rear-open",
    liftType: "ELEVATOR",
    icon: "elFrontCloseRearOpen",
    label: "Rear Door Open",
  },
  {
    key: "el-both-open",
    liftType: "ELEVATOR",
    icon: "elFrontOpenRearOpen",
    label: "Front and Rear Open",
  },
  { key: "el-out", liftType: "ELEVATOR", icon: "elOutService", label: "Out of Service" },
  { key: "es-up", liftType: "ESCALATOR", icon: "esUp", label: "Escalator Up" },
  { key: "es-down", liftType: "ESCALATOR", icon: "esDown", label: "Escalator Down" },
  { key: "es-stop", liftType: "ESCALATOR", icon: "esStopped", label: "Escalator Stopped" },
  { key: "we-forward", liftType: "MOVINGWALK", icon: "weFordward", label: "Moving Walk Forward" },
  { key: "we-backward", liftType: "MOVINGWALK", icon: "weBackward", label: "Moving Walk Backward" },
  { key: "we-stop", liftType: "MOVINGWALK", icon: "weStopped", label: "Moving Walk Stopped" },
];

const LEFT_COLUMN_KEYS = [
  "el-up",
  "el-down",
  "el-closed",
  "el-front-open",
  "el-rear-open",
  "el-both-open",
  "el-out",
];

function LegendEntry({ item }: { item: LegendItem }) {
  return (
    <div
      key={item.key}
      style={{
        display: "grid",
        gridTemplateColumns: "28px 1fr",
        alignItems: "center",
        columnGap: 8,
      }}
    >
      <IconDefinition
        liftType={item.liftType}
        icon={item.icon}
        iconSize={22}
        isMap={false}
        expanded={false}
      />
      <span style={{ fontSize: 12, lineHeight: 1.2 }}>{item.label}</span>
    </div>
  );
}

function MapDisplayLegend({ overlay = false }: { overlay?: boolean }) {
  const [isLandscape, setIsLandscape] = useState<boolean>(
    () => window.innerWidth > window.innerHeight,
  );

  useEffect(() => {
    const onResize = () => {
      setIsLandscape(window.innerWidth > window.innerHeight);
    };

    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, []);

  const leftColumnItems = useMemo(
    () =>
      LEFT_COLUMN_KEYS.map((key) => LEGEND_ITEMS.find((item) => item.key === key)).filter(
        Boolean,
      ) as LegendItem[],
    [],
  );

  const leftColumnSet = useMemo(() => new Set(LEFT_COLUMN_KEYS), []);
  const rightColumnItems = useMemo(
    () => LEGEND_ITEMS.filter((item) => !leftColumnSet.has(item.key)),
    [leftColumnSet],
  );

  const asideStyle: React.CSSProperties = {
    position: overlay ? "relative" : "fixed",
    top: overlay ? undefined : 16,
    right: overlay ? undefined : 16,
    bottom: overlay ? undefined : 16,
    left: overlay ? undefined : undefined,
    zIndex: 2200,
    width: 280,
    maxHeight: overlay ? "45vh" : "40vh",
    overflowY: "auto",
    borderRadius: 6,
    border: "1px solid #374151",
    padding: 12,
    background: "#ffffff",
    color: "#000000",
    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.35)",
    backdropFilter: "blur(4px)",
    pointerEvents: "auto",
  };

  return (
    <aside style={asideStyle} aria-label="Map icon legend">
      <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Map Legend</div>
      {!isLandscape ? (
        <div style={{ display: "grid", gap: 8 }}>
          {LEGEND_ITEMS.map((item) => (
            <LegendEntry key={item.key} item={item} />
          ))}
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <div style={{ display: "grid", gap: 8 }}>
            {leftColumnItems.map((item) => (
              <LegendEntry key={item.key} item={item} />
            ))}
          </div>
          <div style={{ display: "grid", gap: 8 }}>
            {rightColumnItems.map((item) => (
              <LegendEntry key={item.key} item={item} />
            ))}
          </div>
        </div>
      )}
    </aside>
  );
}

export default MapDisplayLegend;
